import { Alert, Box, Text } from '@moonpig/launchpad-components'
import { keyframes, styled } from '@moonpig/launchpad-utils'
import React, { useEffect, useRef } from 'react'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { useProductsLocaleText } from '../../locale'

const fadeInAnimation = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const fadeOutAnimation = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`
const ANIMATION_DURATION_MS = 500

const AlertContainer = styled(Box)<{ animation: { getName(): string } }>`
  position: relative;
  width: 100%;
  pointer-events: all;
  opacity: 0;

  animation: ${({ animation }) => animation} ${ANIMATION_DURATION_MS}ms forwards;
`

type SelectAmountWarningProps = {
  selectedAmountWarning: boolean
  slideOut: boolean
  setSlideOut: (value: boolean) => void
  setSelectedAmountWarning: (value: boolean) => void
  triggerRef?: React.Ref<HTMLInputElement>
  copyOverrides?: {
    selectAmountToContinue?: string
    closeSelectAmountWarning?: string
  }
}

export const SelectAmountWarning: React.FC<SelectAmountWarningProps> = ({
  selectedAmountWarning,
  slideOut,
  setSlideOut,
  setSelectedAmountWarning,
  triggerRef,
  copyOverrides,
}) => {
  const t = useProductsLocaleText()

  const alertRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (selectedAmountWarning && alertRef.current) {
      alertRef.current.focus()
      alertRef.current.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [selectedAmountWarning])

  React.useEffect(() => {
    if (selectedAmountWarning && !slideOut) {
      trackGAEvent({
        event: 'generic_error',
        event_data: {
          action: 'digital gift error',
          category: 'error',
          label: 'value not selected',
          value: undefined,
        },
        error_data: {
          id: '400',
          message: 'select an amount to continue',
        },
      })
    }
  }, [selectedAmountWarning, slideOut])
  return (
    <Box ref={alertRef}>
      {selectedAmountWarning ? (
        <AlertContainer
          data-testid="variant-selector-warning-alert"
          animation={slideOut ? fadeOutAnimation : fadeInAnimation}
          onAnimationEnd={() => {
            return slideOut && setSelectedAmountWarning(false)
          }}
        >
          <Alert
            variant="warning"
            onClickClose={() => {
              setSlideOut(true)
            }}
            labels={{
              close:
                copyOverrides?.closeSelectAmountWarning ??
                t('price_variant_selector.close_select_amount_warning'),
            }}
            my={5}
            // eslint-disable-next-line no-warning-comments
            // TODO: Launchpad need to support nullable triggerRef for R19
            triggerRef={triggerRef as React.RefObject<HTMLElement>}
          >
            <Text>
              {copyOverrides?.selectAmountToContinue ??
                t('price_variant_selector.select_amount_to_continue')}
            </Text>
          </Alert>
        </AlertContainer>
      ) : null}
    </Box>
  )
}
